import React from 'react';
import type { FC } from 'react';
import { Text, Box, Link, UnorderedList, Learn } from '@wix/cli-ui-kit';
import { Trans, useTranslation } from '@wix/cli-i18n';
import type { IPackageManager } from '@wix/package-manager';
import type { AppData } from '../types.js';

export const FinishedSuccessfullyMessage: FC<{
  packageName: string;
  appData: AppData;
  packageManager: IPackageManager;
}> = ({ packageName, appData: { appName, appId }, packageManager }) => {
  const { t } = useTranslation();

  return (
    <Box
      borderColor="blue"
      borderStyle="round"
      flexDirection="column"
      paddingY={1}
      paddingX={3}
      gap={1}
      marginLeft={-2}
      // We need this margin bottom, so yarn will not write over the bottom border of the frame.
      marginBottom={1}
    >
      <Box marginTop={-2} marginLeft={-4}>
        <Text skin="question" bold>
          {t('create_app.results')}{' '}
        </Text>
      </Box>
      <Text>
        <Trans
          i18nKey="create_app.generate_project.finished.ready_to_start"
          components={[<Text bold />]}
          values={{ appName }}
        />
      </Text>

      <Text bold>{t('create_app.generate_project.finished.next_steps')}</Text>

      <UnorderedList gap={1}>
        <UnorderedList.Item marker="👟">
          <Text>
            {t('create_app.generate_project.finished.run_local_development')}
          </Text>
          <Text skin="info">{`cd ${packageName}`}</Text>
          <Text skin="info">{`${packageManager.getRunCmd()} dev`}</Text>
        </UnorderedList.Item>

        <UnorderedList.Item marker="🎯">
          <Text>
            {t('create_app.generate_project.finished.generate_extensions')}
          </Text>
          <Text skin="info">{`${packageManager.getRunCmd()} generate`}</Text>
        </UnorderedList.Item>

        <UnorderedList.Item marker="🤝">
          <Trans
            i18nKey="create_app.create_new.app_on_dev_center"
            components={[
              <Link
                skin="info"
                url={`https://dev.wix.com/apps/${appId}/home`}
              />,
            ]}
          />
        </UnorderedList.Item>

        <UnorderedList.Item marker={<Learn />}>
          <Trans
            i18nKey="create_app.generate_project.finished.visit_docs"
            components={[
              <Link
                skin="info"
                url="https://dev.wix.com/docs/build-apps/developer-tools/cli/get-started/about-the-wix-cli-for-apps"
              />,
            ]}
          />
        </UnorderedList.Item>

        <UnorderedList.Item marker="💬">
          <Trans
            i18nKey="create_app.generate_project.finished.join_community"
            components={[
              <Link skin="info" url="https://discord.gg/wixstudio" />,
            ]}
          />
        </UnorderedList.Item>
      </UnorderedList>
    </Box>
  );
};
